'use client';

import { FC, PropsWithChildren, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { SearchParams } from '@/constants/listing-search-keys';
import { useSearch } from '@/context/listing-search-provider';
import { jsLocalDateToYMD } from '@/utils/date';
import { CalendarDate } from '@/utils/platform-api/__generated__/graphql';
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import clsx, { ClassValue } from 'clsx';
import {
  differenceInDays,
  isAfter,
  isBefore,
  isSameDay,
  parseISO,
  subDays,
} from 'date-fns';
import { DateRange, Day, MonthCaption, MonthGrid } from 'react-day-picker';
import { ClientBrandConfig } from 'types/brand-config';

import DatePicker from '@/components/date-picker';
import DateSearchFooter from '@/components/date-picker/date-search-footer';
import { DayButtonWithTooltip } from '@/components/date-picker/day-button-with-tooltip';
import MobileYearNavigation from '@/components/date-picker/mobile-year-navigation';
import { useDatePickerMonth } from '@/components/date-picker/use-date-picker-month';

import {
  checkAfterNextUnavailable,
  checkBeforePriorUnavailable,
  setNextUnavailable,
  setPriorUnavailable,
} from '../utils/date-availability';
import { formatBlockableDaySquare } from '../utils/day-formatting';
import { checkMaxStayRule, checkMinStayRule } from '../utils/stay-rules';

const tooltipsEnabled = true;

type DateRadius = 'default' | 'square' | 'square-left' | 'square-right';
type DateStyle = 'white' | 'inline' | 'gray';

interface DateRangePopoverProps {
  radius?: DateRadius;
  style?: DateStyle;
  numberOfMonths: number;
  hideWeekdays?: boolean;
  hideNavigation?: boolean;
  direction?: 'horizontal' | 'vertical';
  modal?: boolean;
  calendarDates?: CalendarDate[];
  hasError?: boolean;
  className?: string;
  pickerClassname?: string;
  brand: ClientBrandConfig;
  popoverClassName?: string;
}
const DateRangePopover: FC<PropsWithChildren<DateRangePopoverProps>> = ({
  children,
  style = 'white',
  radius = 'default',
  numberOfMonths,
  hideWeekdays = false,
  direction,
  hideNavigation,
  modal,
  calendarDates,
  hasError,
  className,
  pickerClassname,
  brand,
  popoverClassName,
}) => {
  const { applySearchParams, selectedDates, clearDateParams, setParam } =
    useSearch();
  const openCalendarMode = !calendarDates;

  const [hoveredDay, setHoveredDay] = useState<Date | undefined>(undefined);
  const [numberOfMonthsState, setNumberOfMonthsState] =
    useState<number>(numberOfMonths);
  const popoverRef = useRef<HTMLElement>(null);
  const pathname = usePathname();
  const closeRef = useRef<HTMLButtonElement>(null);
  const background =
    style === 'white'
      ? '#FFFFFF'
      : style === 'gray'
        ? '#F5F5F5'
        : 'transparent';
  const dateStyle = {
    border: 'none',
    backgroundColor: selectedDates?.from ? background : 'transparent',
    ...(radius === 'default' && { borderRadius: ' 0.375rem' }),
    ...(radius === 'square' && { borderRadius: 0 }),
    ...(radius === 'square-left' && {
      borderRadius: '0rem 0.375rem 0.375rem 0rem',
    }),
    ...(radius === 'square-right' && {
      borderRadius: '0.375rem 0rem 0rem 0.375rem',
    }),
  };
  const isValidStay = (a?: DateRange) => {
    if (!calendarDates) {
      return true;
    }
    if (!a?.from || !a?.to || !isValidArrival(a.from)) {
      return false;
    }
    const nights = differenceInDays(a.to, a.from);
    if (nights < brand.minStay) {
      return false;
    }
    return (
      calendarDatesMap?.[jsLocalDateToYMD(a.from)]?.stay_ranges?.find((sr) => {
        return nights >= sr.first && nights <= sr.last;
      }) !== undefined
    );
  };
  const isValidArrival = (arrive: Date) => {
    if (!calendarDates) {
      return true;
    }
    return (
      calendarDatesMap?.[jsLocalDateToYMD(arrive)]?.stay_ranges !== undefined
    );
  };
  const handleSelectDates = (d?: DateRange) => {
    setHoveredDay(undefined);
    if (
      d?.from &&
      selectedDates?.from &&
      selectedDates?.to &&
      (isBefore(d.from, selectedDates.from) || isAfter(d.from, selectedDates.to))
    ) {
      setParam(SearchParams.ARRIVE, d.from);
      setParam(SearchParams.DEPART, undefined);
      return;
    }
    if (d && d?.from && d?.to && !isValidStay(d)) {
      // Selected dates are not valid, determine where we should set the arrival date
      const beforeSelected =
        selectedDates?.from && isBefore(selectedDates?.from, d.from);
      setParam(
        SearchParams.ARRIVE,
        !calendarDates
          ? d.to
          : beforeSelected
            ? isValidArrival(d.from)
              ? d.from
              : undefined
            : isValidArrival(d.to)
              ? d.to
              : undefined
      );
      setParam(SearchParams.DEPART, undefined);
    } else {
      setParam(SearchParams.ARRIVE, d?.from);
      setParam(SearchParams.DEPART, d?.to);
    }
  };

  const calendarDatesMap = useMemo(() => {
    return calendarDates?.reduce(
      (acc, d) => {
        acc[d.cal_date] = d;
        return acc;
      },
      {} as Record<string, CalendarDate>
    );
  }, [calendarDates]);

  const {
    visibleMonth,
    setVisibleMonth,
    initializeVisibleMonth,
    scrollToMonth,
    scrollRequestId,
  } = useDatePickerMonth(selectedDates?.from, { direction });

  const nextUnavailableDate = !selectedDates
    ? undefined
    : setNextUnavailable(calendarDatesMap, selectedDates);
  const priorUnavailableDate = !selectedDates
    ? undefined
    : setPriorUnavailable(calendarDatesMap, selectedDates);

  return (
    <Popover
      ref={popoverRef}
      className={clsx('relative h-full w-full', popoverClassName)}
    >
      <PopoverButton
        as="div"
        data-availability-date-trigger
        className={clsx(
          'relative flex h-full cursor-pointer p-0',
          hasError && '!border !border-error-dark',
          className
        )}
        onClick={() => {
          initializeVisibleMonth();
          setNumberOfMonthsState(numberOfMonths);
        }}
        style={dateStyle}
      >
        {children}
      </PopoverButton>

      <PopoverPanel
        as="div"
        transition
        unmount
        modal={modal}
        className={clsx(
          'fixed left-0 top-0 !z-50 lg:absolute lg:left-1/2 lg:top-12',
          'h-full w-full max-w-[100dvw] !overflow-hidden rounded-xl border bg-white shadow-md transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in lg:mt-2 lg:h-fit lg:w-fit lg:-translate-x-1/4',
          pickerClassname
        )}
      >
        <div
          className={clsx(
            'no-scrollbar relative h-fit max-h-dvh overflow-y-scroll pb-[8rem] lg:pb-0'
          )}
        >
          <div className="sticky top-0 z-20 bg-neutral-light lg:hidden">
            <nav className="flex h-[4.35rem] border-b">
              <CloseButton
                ref={closeRef}
                className="flex h-full w-full items-center justify-start p-5 outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="20"
                  viewBox="0 0 12 20"
                  fill="none"
                >
                  <path
                    d="M0.326417 9.20971C-0.108806 9.64494 -0.108806 10.3487 0.326417 10.7793L9.21608 19.6736C9.6513 20.1088 10.3551 20.1088 10.7857 19.6736C11.2163 19.2384 11.2209 18.5346 10.7857 18.104L2.6831 9.99682L10.7903 1.89426C11.2255 1.45904 11.2255 0.755276 10.7903 0.324683C10.3551 -0.10591 9.6513 -0.11054 9.22071 0.324683L0.326417 9.20971Z"
                    fill="#89888F"
                  />
                </svg>
              </CloseButton>
            </nav>
          </div>
          <div className="pt-4 lg:pt-0">
            <DatePicker
              brand={brand}
              min={brand.minStay}
              month={visibleMonth}
              scrollToMonth={direction === 'vertical' ? scrollToMonth : undefined}
              scrollRequestId={scrollRequestId}
              onMonthChange={setVisibleMonth}
              direction={direction}
              hideWeekdays={hideWeekdays}
              hideNavigation={hideNavigation}
              selected={selectedDates}
              numberOfMonths={numberOfMonthsState}
              setSelected={handleSelectDates}
              onDayMouseEnter={(day) => {
                // The hover behavior breaks on mobile.  Don't use in vertical mode.
                if (direction === 'vertical') {
                  return;
                }
                //NOTE: This would cause infinite re-render if the hoveredDay was replaced with the same date.
                //      Now we early out if the date hasn't changed.
                if (hoveredDay && isSameDay(hoveredDay, day)) {
                  return;
                }
                setHoveredDay(day);
              }}
              components={{
                // ===========================================================================
                //   DAY (just the square). Set the background and font styles.
                // ===========================================================================
                Day: (props) => {
                  const classes: ClassValue[] = [];

                  const priorCalDate =
                    calendarDatesMap?.[
                    jsLocalDateToYMD(subDays(props.day.date, 1))
                    ];

                  const priorAvailable = priorCalDate?.available === true;

                  if (props.className) {
                    classes.push(props.className);
                  }

                  if (
                    props.modifiers.selected &&
                    selectedDates?.from &&
                    isSameDay(props.day.date, selectedDates.from)
                  ) {
                    if (
                      !hoveredDay ||
                      isAfter(hoveredDay, props.day.date) ||
                      isSameDay(hoveredDay, props.day.date)
                    ) {
                      classes.push('is-selected-checkin');
                    }
                    if (hoveredDay && isAfter(hoveredDay, props.day.date)) {
                      classes.push('hovered-day');
                    }
                  }

                  const isCheckinMode =
                    !selectedDates?.from ||
                    (selectedDates?.from &&
                      !selectedDates?.to &&
                      isBefore(props.day.date, selectedDates.from)) ||
                    (selectedDates?.from &&
                      selectedDates?.to &&
                      (isBefore(props.day.date, selectedDates?.to) ||
                        (isAfter(props.day.date, selectedDates.to) &&
                          props.modifiers?.checkin)));

                  const isSquareUnderMouse =
                    hoveredDay && isSameDay(props.day.date, hoveredDay);
                  if (isCheckinMode) {
                    // CHECK-IN MODE
                    if (!openCalendarMode) {
                      formatBlockableDaySquare(
                        props,
                        priorAvailable,
                        classes,
                        true
                      );

                      if (props.modifiers.hoveredDay) {
                        classes.push('hovered-day');
                      }
                      if (props.modifiers.checkin && isSquareUnderMouse) {
                        classes.push('is-selected-checkin');
                      }
                      if (
                        selectedDates?.from &&
                        !selectedDates?.to &&
                        checkBeforePriorUnavailable(priorUnavailableDate, props)
                      ) {
                        classes.push('is-disabled');
                      }
                    } else {
                      if (!props.modifiers.past) {
                        classes.push('dp-available-open');
                      }
                    }
                  } else {
                    // CHECK-OUT MODE
                    if (!openCalendarMode) {
                      formatBlockableDaySquare(
                        props,
                        priorAvailable,
                        classes,
                        false
                      );
                    }

                    if (props.modifiers.hoveredDay) {
                      if (props.modifiers.available) {
                        classes.push('available');
                        if (!isSquareUnderMouse) {
                          classes.push('hovered-day');
                        }
                      }
                      if (props.modifiers.checkout) {
                        if (
                          isSquareUnderMouse &&
                          selectedDates?.from &&
                          !isSameDay(props.day.date, selectedDates.from)
                        ) {
                          // @Ray: No bold on the final circle in this mode; is a style missing/broken?
                          //       We're also missing the fancy fade-in from the circle to the adjacent rect.
                          classes.push('is-selected-checkout');
                        }
                      }
                    }

                    const minStay = selectedDates?.from
                      ? calendarDatesMap?.[jsLocalDateToYMD(selectedDates.from)]
                        ?.min_stay
                      : undefined;

                    const minStayMet = checkMinStayRule(
                      minStay,
                      props.day.date,
                      selectedDates
                    );
                    if (!minStayMet) {
                      classes.push('is-disabled');
                      classes.push('min-stay-not-met');
                    }

                    if (!props.modifiers.checkout && !props.modifiers.selected) {
                      classes.push('is-disabled');
                    } else if (
                      selectedDates?.from &&
                      isBefore(props.day.date, selectedDates.from)
                    ) {
                      classes.push('is-disabled');
                    } else if (
                      checkAfterNextUnavailable(nextUnavailableDate, props)
                    ) {
                      classes.push('is-disabled');
                    } else {
                      if (!openCalendarMode) {
                        classes.push('available');
                      } else if (!props.modifiers.past) {
                        classes.push('dp-available-open');

                        const brandMinStayMet = checkMinStayRule(
                          brand.minStay,
                          props.day.date,
                          selectedDates
                        );
                        if (!brandMinStayMet) {
                          classes.push('is-disabled');
                        }
                      }
                    }
                  }

                  if (props.modifiers.past) {
                    classes.push('is-disabled');
                    classes.push('dp-past-strikethrough');
                  }

                  return <Day {...props} className={clsx(...classes)} />;
                },
                // ===========================================================================
                //   DAY 'BUTTON' (it's a div). Set the DOM enabled state and tooltip.
                // ===========================================================================
                DayButton: (props) => {
                  let tooltipText: string | undefined = undefined;
                  const isCheckinMode =
                    !selectedDates?.from ||
                    (selectedDates?.from &&
                      !selectedDates?.to &&
                      isBefore(props.day.date, selectedDates.from)) ||
                    (selectedDates?.from &&
                      selectedDates?.to &&
                      (isBefore(props.day.date, selectedDates?.to) ||
                        (isAfter(props.day.date, selectedDates.to) &&
                          props.modifiers?.checkin)));

                  let dayButtonEnabled = true;

                  if (openCalendarMode) {
                    if (isCheckinMode) {
                      if (
                        props.modifiers.past ||
                        (selectedDates?.from &&
                          !selectedDates?.to &&
                          checkBeforePriorUnavailable(
                            priorUnavailableDate,
                            props
                          ))
                      ) {
                        dayButtonEnabled = false;
                      }
                    } else {
                      if (
                        selectedDates?.from &&
                        isBefore(props.day.date, selectedDates.from)
                      ) {
                        dayButtonEnabled = false;
                    } else if (
                      selectedDates?.from &&
                      isSameDay(props.day.date, selectedDates.from)
                    ) {
                      // Selected check-in date - no tooltip needed
                    } else {
                        const minStayMet = checkMinStayRule(
                          brand.minStay,
                          props.day.date,
                          selectedDates
                        );
                        if (!minStayMet) {
                          dayButtonEnabled = false;

                          if (tooltipsEnabled) {
                            tooltipText = `Min stay of ${brand.minStay} nights`;
                          }
                        }
                      }
                    }
                  } else {
                    if (isCheckinMode) {
                      const minStay =
                        calendarDatesMap?.[jsLocalDateToYMD(props.day.date)]
                          ?.min_stay;
                      // CHECK-IN MODE
                      if (
                        !props.modifiers.checkin ||
                        (selectedDates?.from &&
                          !selectedDates?.to &&
                          checkBeforePriorUnavailable(
                            priorUnavailableDate,
                            props
                          ))
                      ) {
                        if (tooltipsEnabled) {
                          tooltipText = props.modifiers.available
                            ? minStay != undefined
                              ? `Min stay of ${minStay} nights`
                              : 'Min stay not met'
                            : 'Check-in unavailable';
                        }

                        dayButtonEnabled = false;
                      }

                      if (props.modifiers.past) {
                        dayButtonEnabled = false;
                      }
                    } else {
                      // CHECK-OUT MODE
                      if (!props.modifiers.checkout) {
                        dayButtonEnabled = false;

                        if (tooltipsEnabled) {
                          tooltipText = 'Check-out unavailable';
                        }
                      }

                      const minStay = selectedDates?.from
                        ? calendarDatesMap?.[jsLocalDateToYMD(selectedDates.from)]
                          ?.min_stay
                        : undefined;

                      const minStayMet = checkMinStayRule(
                        minStay,
                        props.day.date,
                        selectedDates
                      );
                      if (!minStayMet) {
                        dayButtonEnabled = false;

                        if (tooltipsEnabled) {
                          tooltipText = `Min stay of ${minStay} nights`;
                        }
                      }

                    if (checkAfterNextUnavailable(nextUnavailableDate, props)) {
                        dayButtonEnabled = false;
                      }

                      // Max stay disclosure
                      if (
                        checkMaxStayRule(
                          brand.maxStay,
                          props.day.date,
                          selectedDates
                        )
                      ) {
                        if (tooltipsEnabled) {
                          tooltipText = `For our best long stay pricing please contact us for stays of longer than ${brand.maxStay} nights.`;
                        }
                      }
                    }
                  }

                  return (
                    <DayButtonWithTooltip
                      disableClick={!dayButtonEnabled}
                      tooltipText={tooltipText}
                      {...props}
                    />
                  );
                },
                MonthCaption: (props) => {
                  return (
                    <MonthCaption {...props} className={clsx(props.className)} />
                  );
                },
                MonthGrid: (props) => {
                  return (
                    <MonthGrid
                      onMouseLeave={() => setHoveredDay(undefined)}
                      {...props}
                    />
                  );
                },
              }}
              modifiers={{
                checkin: (date) => {
                  if (openCalendarMode) {
                    return true;
                  }
                  const thisCalDate = calendarDatesMap?.[jsLocalDateToYMD(date)];
                  return (thisCalDate?.stay_ranges?.length || 0) > 0;
                },
                checkout: (date) => {
                  const thisCalDate = calendarDatesMap?.[jsLocalDateToYMD(date)];
                  if (openCalendarMode) {
                    return true;
                  } else if (selectedDates?.from && !selectedDates?.to) {
                    return isValidStay({ from: selectedDates.from, to: date });
                  } else if (selectedDates?.from && selectedDates?.to) {
                    return thisCalDate?.checkout == true;
                  }
                  return false;
                },
                available: (date) => {
                  if (openCalendarMode) {
                    return true;
                  }
                  const thisCalDate = calendarDatesMap?.[jsLocalDateToYMD(date)];
                  return thisCalDate?.available == true;
                },
                past: { before: new Date() },
                hoveredDay: (date) => {
                  if (selectedDates?.from && hoveredDay) {
                    if (selectedDates?.to) {
                      const hoveredBeforeFrom = isBefore(hoveredDay, selectedDates.from);
                      const hoveredWithinRange =
                        !isBefore(hoveredDay, selectedDates.from) &&
                        !isAfter(hoveredDay, selectedDates.to);
                      const lastAvailableInBlock = nextUnavailableDate
                        ? subDays(
                          parseISO(nextUnavailableDate.cal_date),
                          1
                        )
                        : null;
                      const hoveredWithinSameBlock =
                        lastAvailableInBlock &&
                        !isBefore(hoveredDay, selectedDates.from) &&
                        !isAfter(hoveredDay, lastAvailableInBlock);
                      if (
                        hoveredBeforeFrom ||
                        (!hoveredWithinRange && !hoveredWithinSameBlock)
                      ) {
                        return false;
                      }
                    }
                    const dateIsAfterActiveCheckIn = isAfter(
                      date,
                      selectedDates.from
                    );
                    const dateIsBeforeHoveredDay = isBefore(date, hoveredDay);
                    const dateIsAfterHoveredDay = isAfter(date, hoveredDay);
                    const dateIsHoveredDay = isSameDay(date, hoveredDay);
                    return (
                      (dateIsAfterActiveCheckIn &&
                        (dateIsBeforeHoveredDay || dateIsHoveredDay)) ||
                      (!dateIsAfterActiveCheckIn &&
                        (dateIsAfterHoveredDay || dateIsHoveredDay))
                    );
                  }
                  return false;
                },
              }}
            />
          </div>
          <MobileYearNavigation
            visibleMonth={visibleMonth}
            setMonth={setVisibleMonth}
          />
        </div>
        <div className="fixed inset-x-0 bottom-0 z-20 bg-neutral-light p-4 lg:sticky lg:bottom-0">
          <DateSearchFooter
            hideFlex
            setMonth={setVisibleMonth}
            routerPath={pathname}
            shouldSubmit
            applySearchParams={applySearchParams}
            clearDateParams={clearDateParams}
            selectedDates={selectedDates}
            setParam={setParam}
          />
        </div>
      </PopoverPanel>
    </Popover>
  );
};

export default DateRangePopover;
